import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  LinkAddressesFieldsFragment,
  LinkAddressesViewDocument,
  LinkAddressesViewQuery,
  SmsSettingsViewDocument,
  SmsSettingsViewQuery,
  useLinkAddressesViewQuery,
  useStoreLinkAddressesMutation,
} from 'core/graphql';

import { ISmsError, smsCrashErrors, toSmsErrors } from './smsFeedback';
import { useSmsPermissions } from './useSmsPermissions';

/**
 * ТЗ 0084 — куда ведут ссылки в SMS и письмах: анкета дофинансирования и
 * LMS (правят Admin и продавцы — `canEdit` говорит сервер), сократитель и
 * Documenso (только чтение: инфраструктура, смена сломала бы выданные
 * ссылки).
 *
 * У `LinkAddressesType` нет `id`: ответ сохранения пишется в корневое поле
 * руками, иначе экран показывал бы старое до следующего запроса.
 */

export type ILinkAddresses = LinkAddressesFieldsFragment;

/**
 * Что сохранить: `undefined` — поле не трогать, пустая строка — вернуть
 * адрес из настроек сервера.
 */
export interface ILinkAddressesInput {
  dofinFormUrl?: string;
  lmsBaseUrl?: string;
}

/**
 * Перепроверка 0084: настройки канала, хвост отписки и адреса ссылок живут
 * в ОДНОЙ строке сервера (`OutreachSettings`) — у неё одно «Изменено …».
 * После любого сохранения перечитываем оба запроса экрана: иначе строка
 * «Изменено» и соседняя секция показывали бы старое до перезахода.
 * `client.query` с `network-only`, а не `refetchQueries` по имени — тот
 * бьёт и по пропущенному запросу (грабли `refetch-by-name-skipped-query`).
 */
export const useRereadSmsSettings = () => {
  const client = useApolloClient();
  return useCallback(() => {
    Promise.all([
      client.query<SmsSettingsViewQuery>({
        query: SmsSettingsViewDocument,
        fetchPolicy: 'network-only',
      }),
      client.query<LinkAddressesViewQuery>({
        query: LinkAddressesViewDocument,
        fetchPolicy: 'network-only',
      }),
    ]).catch(() => undefined);
  }, [client]);
};

export const useLinkAddresses = () => {
  const { canUse } = useSmsPermissions();
  const reread = useRereadSmsSettings();
  const query = useLinkAddressesViewQuery({
    skip: !canUse,
    fetchPolicy: 'cache-and-network',
  });
  const [saveMut, { loading: saving }] = useStoreLinkAddressesMutation();

  const save = useCallback(
    async (
      input: ILinkAddressesInput,
    ): Promise<{ ok: boolean; errors: ISmsError[] }> => {
      try {
        const { data } = await saveMut({
          variables: {
            dofinFormUrl: input.dofinFormUrl ?? null,
            lmsBaseUrl: input.lmsBaseUrl ?? null,
          },
          update: (cache, { data: answer }) => {
            const next = answer?.saveLinkAddresses?.linkAddresses;
            if (!next) return;
            cache.writeQuery<LinkAddressesViewQuery>({
              query: LinkAddressesViewDocument,
              data: { linkAddresses: next },
            });
          },
        });
        const payload = data?.saveLinkAddresses;
        const errors = toSmsErrors(payload?.customErrors);
        const ok = Boolean(payload?.success) && !errors.length;
        if (ok) reread();
        return { ok, errors };
      } catch (caught) {
        return { ok: false, errors: smsCrashErrors(caught) };
      }
    },
    [reread, saveMut],
  );

  const { refetch } = query;
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);

  return {
    addresses: query.data?.linkAddresses ?? null,
    loading: query.loading && !query.data,
    error: query.error,
    reload,
    save,
    saving,
  };
};
